import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Image as ImageIcon, Plus, Trash2,
  ChevronLeft, ChevronRight, RefreshCw, Loader2
} from 'lucide-react';
import { API_URL } from '@/config/api';

export default function HeroSlidesEditor() {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [dirty, setDirty] = useState(false);
  const replaceInputRef = useRef(null);
  const addInputRef = useRef(null);
  const replaceTargetRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/admin/hero-slides`)
      .then(res => setSlides(res.data.slides || []))
      .catch(() => toast.error('Failed to load slides'))
      .finally(() => setLoading(false));
  }, []);

  const markDirty = () => setDirty(true);

  const updateCaption = (i, caption) => {
    setSlides(prev => prev.map((s, idx) => idx === i ? { ...s, caption } : s));
    markDirty();
  };

  const moveSlide = (i, dir) => {
    setSlides(prev => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    markDirty();
  };

  const deleteSlide = (i) => {
    if (!window.confirm(`Remove Slide ${i + 1} from the slideshow?`)) return;
    setSlides(prev => prev.filter((_, idx) => idx !== i));
    markDirty();
    toast.success('Slide removed. Click Save All to make it permanent.');
  };

  const uploadPhoto = async (file, targetIndex) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setUploadingIndex(targetIndex === null ? 'new' : targetIndex);
    try {
      const res = await axios.post(`${API_URL}/admin/hero-slides/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data.url;
      if (targetIndex === null) {
        setSlides(prev => [...prev, { image: url, caption: '' }]);
        toast.success('New photo added at the end. Click Save All to publish.');
      } else {
        setSlides(prev => prev.map((s, idx) => idx === targetIndex ? { ...s, image: url } : s));
        toast.success(`Slide ${targetIndex + 1} photo replaced. Click Save All to publish.`);
      }
      markDirty();
    } catch (e) {
      toast.error('Photo upload failed. Please try again.');
    } finally {
      setUploadingIndex(null);
    }
  };

  const onReplaceFile = (e) => {
    const file = e.target.files && e.target.files[0];
    uploadPhoto(file, replaceTargetRef.current);
    e.target.value = '';
  };

  const onAddFile = (e) => {
    const file = e.target.files && e.target.files[0];
    uploadPhoto(file, null);
    e.target.value = '';
  };

  const startReplace = (i) => {
    replaceTargetRef.current = i;
    replaceInputRef.current && replaceInputRef.current.click();
  };

  const saveAll = async () => {
    if (slides.length === 0) {
      toast.error('You need at least 1 slide in the slideshow.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/hero-slides`, { slides });
      setDirty(false);
      toast.success('Slideshow saved! It is now live on your Home page.');
    } catch (e) {
      toast.error('Failed to save slideshow');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="hero-slides-editor">
      {/* hidden file inputs */}
      <input ref={replaceInputRef} type="file" accept="image/*" className="hidden" onChange={onReplaceFile} data-testid="hero-replace-file-input" />
      <input ref={addInputRef} type="file" accept="image/*" className="hidden" onChange={onAddFile} data-testid="hero-add-file-input" />

      <div className="bg-card border-b border-border px-6 py-4 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/site-manager')}
              className="p-2 rounded-lg hover:bg-muted"
              data-testid="hero-slides-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ImageIcon className="w-6 h-6 text-primary" /> Home Slideshow Editor
              </h1>
              <p className="text-muted-foreground text-sm">
                Replace photos, add new ones, remove slides, change their order, and write captions.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => addInputRef.current && addInputRef.current.click()}
              disabled={uploadingIndex !== null}
              className="flex items-center gap-2 px-5 py-3 bg-card border-2 border-primary text-primary rounded-lg font-bold hover:bg-primary/10 disabled:opacity-50"
              data-testid="hero-add-photo-btn"
            >
              {uploadingIndex === 'new' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              Add Photo
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold disabled:opacity-50 ${dirty ? 'bg-primary text-black hover:bg-primary/90 ring-2 ring-primary/50' : 'bg-primary text-black hover:bg-primary/90'}`}
              data-testid="hero-slides-save-btn"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : dirty ? 'Save All (unsaved changes)' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      {dirty && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          <p className="text-sm text-primary font-semibold" data-testid="hero-unsaved-warning">
            You have unsaved changes — click "Save All" to publish them to your Home page.
          </p>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {slides.map((slide, i) => (
          <div key={`${slide.image}-${i}`} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`hero-slide-card-${i}`}>
            <div className="relative aspect-video bg-black">
              <img src={slide.image} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs font-bold rounded">
                Slide {i + 1}
              </span>
              {uploadingIndex === i && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              )}
            </div>

            {/* Photo tools */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => moveSlide(i, -1)}
                  disabled={i === 0}
                  title="Move earlier in the slideshow"
                  className="p-2 rounded-lg hover:bg-muted disabled:opacity-30"
                  data-testid={`hero-slide-move-left-${i}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveSlide(i, 1)}
                  disabled={i === slides.length - 1}
                  title="Move later in the slideshow"
                  className="p-2 rounded-lg hover:bg-muted disabled:opacity-30"
                  data-testid={`hero-slide-move-right-${i}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => startReplace(i)}
                  disabled={uploadingIndex !== null}
                  title="Replace this photo"
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-40"
                  data-testid={`hero-slide-replace-btn-${i}`}
                >
                  <RefreshCw className="w-4 h-4" /> Replace
                </button>
                <button
                  onClick={() => deleteSlide(i)}
                  title="Remove this slide"
                  className="p-2 rounded-lg text-red-500 hover:bg-red-500/10"
                  data-testid={`hero-slide-delete-btn-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <textarea
                value={slide.caption || ''}
                onChange={(e) => updateCaption(i, e.target.value)}
                placeholder="Type your caption for this photo..."
                rows={2}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid={`hero-slide-caption-input-${i}`}
              />
            </div>
          </div>
        ))}

        {/* Add card */}
        <button
          onClick={() => addInputRef.current && addInputRef.current.click()}
          disabled={uploadingIndex !== null}
          className="border-2 border-dashed border-border rounded-xl min-h-[260px] flex flex-col items-center justify-center gap-3 text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
          data-testid="hero-add-photo-card"
        >
          {uploadingIndex === 'new' ? <Loader2 className="w-10 h-10 animate-spin" /> : <Plus className="w-10 h-10" />}
          <span className="font-semibold">Add a new photo</span>
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-10">
        <button
          onClick={saveAll}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-black rounded-lg font-bold hover:bg-primary/90 disabled:opacity-50"
          data-testid="hero-slides-save-btn-bottom"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save All'}
        </button>
      </div>
    </div>
  );
}
